/**
 * Structural shape used by the display helpers. Accepts both `User`
 * (from `/api/users`) and `UserInfo` (from `/me`) — they diverge slightly
 * on `null` vs `undefined` for optional fields.
 */
type Displayable = {
  firstName?: string | null;
  lastName?: string | null;
  username?: string | null;
  email?: string | null;
};

/**
 * Two-letter initials used as avatar fallback when `avatarUrl` is empty.
 * Uses firstName + lastName first chars; falls back to the first two
 * letters of `username` or `email`; finally `?` if nothing is available.
 */
export function initials(u: Displayable | null): string {
  if (!u) return '?';
  const first = u.firstName?.charAt(0) ?? '';
  const last = u.lastName?.charAt(0) ?? '';
  const fromName = (first + last).trim();
  if (fromName) return fromName.toUpperCase();
  const fallback = u.username ?? u.email ?? '';
  return fallback.slice(0, 2).toUpperCase() || '?';
}

/**
 * Display name: `firstName lastName` if either is set, otherwise `username`.
 */
export function fullName(u: Displayable | null): string {
  if (!u) return '';
  return [u.firstName, u.lastName].filter(Boolean).join(' ') || u.username || '';
}
